import React, { useState, useEffect } from 'react';
import { Search, MapPin, Car, Clock, Filter } from 'lucide-react';
import { useDateFilter } from '../../contexts/DateFilterContext';
import config from '../../config';
import './LockedAssignments.css';

const LockedAssignments = () => {
  const { isDateInRange } = useDateFilter();
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Fetch assignment data from API
  useEffect(() => {
    const fetchAssignments = async (isInitialLoad = false) => {
      try {
        if (isInitialLoad) setLoading(true);
        const response = await fetch(`${config.dispatchApiBase}/assignment-states`);
        const data = await response.json();
        
        if (data.success) {
          // Transform the data to match our component structure
          const transformedAssignments = data.data.map(assignment => ({
            id: assignment.id,
            booking_id: assignment.booking_id,
            vehicle_id: assignment.vehicle_id,
            vehicle_type: assignment.vehicle_type || 'class2',
            pickup_location: assignment.pickup_location || 'Unknown Location',
            pickup_lat: assignment.pickup_lat,
            pickup_lng: assignment.pickup_lon,
            drop_location: assignment.drop_location || 'Unknown Location',
            drop_lat: assignment.drop_lat,
            drop_lng: assignment.drop_lon,
            pickup_time: assignment.pickup_time,
            distance: assignment.distance_km,
            status: assignment.state,
            estimated_reach_time: assignment.estimated_reach_time
          }));
          
          setAssignments(transformedAssignments);
          setFilteredAssignments(transformedAssignments);
          setConnectionError(false);
          setLastUpdated(new Date());
        } else {
          console.error('Failed to fetch assignments:', data.error);
          setAssignments([]);
          setFilteredAssignments([]);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setConnectionError(true);
        // Don't clear existing data on connection error, keep showing last known state
        if (assignments.length === 0) {
          setAssignments([]);
          setFilteredAssignments([]);
        }
      } finally {
        if (isInitialLoad) setLoading(false);
      }
    };

    fetchAssignments(true); // Initial load
    
    // Set up polling for real-time updates every 5 seconds
    const interval = setInterval(fetchAssignments, 5000);
    
    return () => clearInterval(interval);
  }, []); // Empty dependency array to prevent re-running

  useEffect(() => {
    let filtered = assignments;

    if (searchTerm) {
      filtered = filtered.filter(assignment =>
        assignment.booking_id.toString().includes(searchTerm) ||
        assignment.vehicle_id.toString().includes(searchTerm) ||
        assignment.pickup_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.drop_location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(assignment => assignment.status === filterStatus);
    }

    // Apply date filter on pickup_time
    filtered = filtered.filter(assignment => {
      const assignmentDate = assignment.pickup_time || assignment.created_at;
      return !assignmentDate || isDateInRange(assignmentDate);
    });

    setFilteredAssignments(filtered);
  }, [assignments, searchTerm, filterStatus, isDateInRange]);

  const openAssignmentModal = (assignment) => {
    setSelectedAssignment(assignment);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeAssignmentModal = () => {
    setSelectedAssignment(null);
    document.body.style.overflow = 'unset'; // Restore scrolling
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeAssignmentModal();
      }
    };

    if (selectedAssignment) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedAssignment]);

  const getStatusColor = (status) => {
    const colors = {
      'assigned': '#3b82f6',
      'en_route_to_pickup': '#f59e0b',
      'arrived_at_pickup': '#10b981',
      'passenger_picked_up': '#8b5cf6',
      'en_route_to_drop': '#06b6d4',
      'completed': '#10b981'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'assigned': 'Assigned',
      'en_route_to_pickup': 'En Route to Pickup',
      'arrived_at_pickup': 'Arrived at Pickup',
      'passenger_picked_up': 'Passenger Picked Up',
      'en_route_to_drop': 'En Route to Drop',
      'completed': 'Completed'
    };
    return labels[status] || status;
  };

  const getVehicleColor = (vehicleType) => {
    const colors = {
      'class1': '#ef4444',
      'class2': '#3b82f6',
      'class3': '#10b981',
      'class4': '#f59e0b',
      'class5': '#8b5cf6',
      'class6': '#06b6d4',
      'class7': '#f97316',
      'class8': '#84cc16',
      'class9': '#ec4899'
    };
    return colors[vehicleType] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading assignments...</p>
      </div>
    );
  }

  return (
    <div className="locked-assignments">
      <div className="page-header">
        <h1>Locked Assignments</h1>
        <p>Visual representation of vehicle assignments and routes</p>
        
        {/* Connection Status */}
        <div className={`connection-status ${connectionError ? 'error' : 'connected'}`}>
          <div className="status-indicator"></div>
          <span>
            {connectionError ? 'Backend Disconnected' : 'Connected'}
            {lastUpdated && !connectionError && (
              <span className="last-updated">
                • Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by booking ID, vehicle ID, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <Filter size={20} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="assigned">Assigned</option>
            <option value="en_route_to_pickup">En Route to Pickup</option>
            <option value="arrived_at_pickup">Arrived at Pickup</option>
            <option value="passenger_picked_up">Passenger Picked Up</option>
            <option value="en_route_to_drop">En Route to Drop</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      <div className="assignments-grid">
        {filteredAssignments.map(assignment => (
          <div key={assignment.id} className="assignment-card">
            <div className="assignment-header">
              <div className="assignment-ids">
                <span className="booking-id">Booking #{assignment.booking_id}</span>
                <span className="vehicle-id" style={{ color: getVehicleColor(assignment.vehicle_type) }}>
                  <Car size={16} />
                  Vehicle {assignment.vehicle_id} ({assignment.vehicle_type})
                </span>
              </div>
              <div className="assignment-status">
                <span 
                  className="status-badge assigned"
                >
                  {getStatusLabel(assignment.status)}
                </span>
              </div>
            </div>

            {/* Compact Summary View */}
            <div className="assignment-summary">
              <div className="summary-info">
                <div className="route-summary">
                  <div className="route-points">
                    <div className="summary-point pickup">
                      <MapPin size={14} />
                      <span>{assignment.pickup_location}</span>
                    </div>
                    <div className="route-arrow">→</div>
                    <div className="summary-point drop">
                      <MapPin size={14} />
                      <span>{assignment.drop_location}</span>
                    </div>
                  </div>
                  <div className="route-stats">
                    <span className="distance-stat">{assignment.distance} km</span>
                    <span className="time-stat">
                      <Clock size={12} />
                      {new Date(assignment.pickup_time).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>
              <button 
                className="view-details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openAssignmentModal(assignment);
                }}
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredAssignments.length === 0 && (
        <div className="no-results">
          <Car size={48} />
          <h3>No assignments found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Assignment Details Modal */}
      {selectedAssignment && (
        <div className="modal-overlay" onClick={closeAssignmentModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <Car size={24} style={{ color: getVehicleColor(selectedAssignment.vehicle_type) }} />
                <h2>Booking #{selectedAssignment.booking_id} - Complete Details</h2>
              </div>
              <button className="modal-close-btn" onClick={closeAssignmentModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Assignment Info Section */}
              <div className="modal-assignment-info">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Booking ID:</label>
                    <span>#{selectedAssignment.booking_id}</span>
                  </div>
                  <div className="info-item">
                    <label>Vehicle:</label>
                    <span style={{ color: getVehicleColor(selectedAssignment.vehicle_type) }}>
                      Vehicle {selectedAssignment.vehicle_id} ({selectedAssignment.vehicle_type})
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span className="status-badge assigned">
                      {getStatusLabel(selectedAssignment.status)}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Distance:</label>
                    <span>{selectedAssignment.distance} km</span>
                  </div>
                  <div className="info-item">
                    <label>Pickup Time:</label>
                    <span>{new Date(selectedAssignment.pickup_time).toLocaleString()}</span>
                  </div>
                  <div className="info-item">
                    <label>Estimated Reach:</label>
                    <span>{new Date(selectedAssignment.estimated_reach_time).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Route Details Section */}
              <div className="modal-route-section">
                <h3>Route Details</h3>
                
                <div className="modal-route-visualization">
                  <div className="modal-route-path">
                    {/* Pickup Point */}
                    <div className="modal-route-item pickup">
                      <div className="route-icon pickup">
                        <MapPin size={16} />
                      </div>
                      <div className="stop-info">
                        <div className="stop-header">
                          <h4>{selectedAssignment.pickup_location}</h4>
                          <span className="location-type">Pickup Location</span>
                        </div>
                        <div className="stop-time">
                          <Clock size={12} />
                          <span>{new Date(selectedAssignment.pickup_time).toLocaleTimeString()}</span>
                        </div>
                        <div className="coordinates">
                          <small>Lat: {selectedAssignment.pickup_lat?.toFixed(4)}, Lng: {selectedAssignment.pickup_lng?.toFixed(4)}</small>
                        </div>
                      </div>
                    </div>

                    {/* Route Connector */}
                    <div className="path-connector">
                      <div 
                        className="connector-line" 
                        style={{ borderColor: getVehicleColor(selectedAssignment.vehicle_type) }}
                      ></div>
                      <div className="distance-badge">
                        {selectedAssignment.distance} km
                      </div>
                    </div>

                    {/* Drop Point */}
                    <div className="modal-route-item dropoff">
                      <div className="route-icon dropoff">
                        <MapPin size={16} />
                      </div>
                      <div className="stop-info">
                        <div className="stop-header">
                          <h4>{selectedAssignment.drop_location}</h4>
                          <span className="location-type">Drop Location</span>
                        </div>
                        <div className="stop-time">
                          <Clock size={12} />
                          <span>ETA: {new Date(selectedAssignment.estimated_reach_time).toLocaleTimeString()}</span>
                        </div>
                        <div className="coordinates">
                          <small>Lat: {selectedAssignment.drop_lat?.toFixed(4)}, Lng: {selectedAssignment.drop_lng?.toFixed(4)}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LockedAssignments;